import type {
  PageScript,
  PageScriptGroupDefinition,
  PageScriptGroups,
  PageScriptGroupsDefinition
} from '@aurora/shared-generated/types/graphql-schema-types';
import { PageScriptGroup } from '@aurora/shared-generated/types/graphql-schema-types';
import type { RouteInfo } from '@aurora/shared-types/community';
import type { Tenant } from '@aurora/shared-types/tenant';
import NextGenUrlBuilder from '@aurora/shared-utils/helpers/urls/UrlHelper/NextGenUrlBuilder';
import { getLog } from '@aurora/shared-utils/log';

const log = getLog(module);

interface ScriptTag {
  id: string;
  src: string;
}

function getEntityIdsFromRouteParams(routeInfo: RouteInfo) {
  const entityIds = [];
  if (routeInfo?.params) {
    if (routeInfo.params['boardId']) {
      entityIds.push('board:' + routeInfo.params['boardId']);
    }
    if (routeInfo.params['categoryId']) {
      entityIds.push('category:' + routeInfo.params['categoryId']);
    }
    if (routeInfo.params['groupHubId']) {
      entityIds.push('groupHub:' + routeInfo.params['groupHubId']);
    }
    if (routeInfo.params['userId']) {
      entityIds.push('user:' + routeInfo.params['userId']);
    }
    if (routeInfo.params['replyId']) {
      entityIds.push('message:' + routeInfo.params['replyId']);
    } else if (routeInfo.params['messageId']) {
      entityIds.push('message:' + routeInfo.params['messageId']);
    }
  }
  return entityIds;
}

function branchQueryParameter(
  tenant: Tenant,
  scriptUrlBuilder: NextGenUrlBuilder,
  branchName: string
) {
  return branchName &&
    tenant.publicConfig.mainBranchName &&
    branchName !== tenant.publicConfig.mainBranchName
    ? scriptUrlBuilder.addQuery({
        ['assets.branch']: branchName
      })
    : scriptUrlBuilder;
}

function getScript(
  tenant: Tenant,
  branchName: string,
  routeInfo: RouteInfo,
  group: PageScriptGroupDefinition,
  pageScript: PageScript
): ScriptTag {
  const { id, enabled, url, dynamic } = pageScript;
  if (enabled) {
    let scriptUrlBuilder = branchQueryParameter(tenant, NextGenUrlBuilder.fromUrl(url), branchName);
    if (dynamic && routeInfo) {
      scriptUrlBuilder = scriptUrlBuilder.addQuery({
        ['page.id']: routeInfo.pageName,
        ['entity.id']: getEntityIdsFromRouteParams(routeInfo)
      });
    }
    const source = scriptUrlBuilder.build();
    log.debug('Loading script %s with src %s in group %s', id, source, group);
    return {
      id,
      src: source
    };
  }
  return null;
}

function getGroupDefinition(
  groupsDefinition: PageScriptGroupsDefinition,
  pageScriptGroup: PageScriptGroup
): PageScriptGroupDefinition {
  switch (pageScriptGroup) {
    case PageScriptGroup.BeforeInteractiveFirst: {
      return groupsDefinition.beforeInteractiveFirst;
    }
    case PageScriptGroup.BeforeInteractiveLast: {
      return groupsDefinition.beforeInteractiveLast;
    }
    case PageScriptGroup.AfterInteractive: {
      return groupsDefinition.afterInteractive;
    }
    case PageScriptGroup.LazyOnLoad: {
      return groupsDefinition.lazyOnLoad;
    }
  }
}

interface ScriptTagGroups {
  [PageScriptGroup.BeforeInteractiveFirst]: ScriptTag[];
  [PageScriptGroup.BeforeInteractiveLast]: ScriptTag[];
  [PageScriptGroup.AfterInteractive]: ScriptTag[];
  [PageScriptGroup.LazyOnLoad]: ScriptTag[];
}

function getScripts(
  tenant: Tenant,
  branchName: string,
  routeInfo: RouteInfo,
  pageScriptGroups: PageScriptGroups,
  pageScriptGroup: PageScriptGroup
): ScriptTag[] {
  if (!pageScriptGroups) {
    return [];
  }
  const { groupsDefinition, pageScripts } = pageScriptGroups;
  if (pageScripts?.length === 0) {
    return [];
  } else {
    const groupDefinition = getGroupDefinition(groupsDefinition, pageScriptGroup);
    return (
      groupDefinition.scriptIds
        .map(pageScriptId => {
          const pageScript = pageScripts.find(script => script.id === pageScriptId);
          if (!pageScript) {
            return null;
          }
          return getScript(tenant, branchName, routeInfo, groupDefinition, pageScript);
        })
        .filter(o => !!o) ?? []
    );
  }
}

function getScriptTagGroups(
  tenant: Tenant,
  branchName: string,
  routeInfo: RouteInfo,
  pageScriptGroups: PageScriptGroups
): ScriptTagGroups {
  return {
    [PageScriptGroup.BeforeInteractiveFirst]: getScripts(
      tenant,
      branchName,
      routeInfo,
      pageScriptGroups,
      PageScriptGroup.BeforeInteractiveFirst
    ),
    [PageScriptGroup.BeforeInteractiveLast]: getScripts(
      tenant,
      branchName,
      routeInfo,
      pageScriptGroups,
      PageScriptGroup.BeforeInteractiveLast
    ),
    [PageScriptGroup.AfterInteractive]: getScripts(
      tenant,
      branchName,
      routeInfo,
      pageScriptGroups,
      PageScriptGroup.AfterInteractive
    ),
    [PageScriptGroup.LazyOnLoad]: getScripts(
      tenant,
      branchName,
      routeInfo,
      pageScriptGroups,
      PageScriptGroup.LazyOnLoad
    )
  };
}

export { getScriptTagGroups, branchQueryParameter };
export type { ScriptTagGroups, ScriptTag };
